import { Building2, Gift, Trophy } from 'lucide-react';
import type { LinkProps } from 'next/link';
import HistoryIcon from '@ping/assets/Icon/history.svg';
import CardIcon from '@ping/assets/Icon/card.svg';
import LogoutIcon from '@ping/assets/Icon/logout.svg';
import PortfolioIcon from '@ping/assets/Icon/portfolio.svg';
import ReferralsIcon from '@ping/assets/Icon/referrals.svg';
import SettingsIcon from '@ping/assets/Icon/settings.svg';
import SupportIcon from '@ping/assets/Icon/support.svg';
import ApiKeysIcon from '@ping/assets/Icon/api.svg';
import AddressBookIcon from '@ping/assets/Icon/address-book.svg';
import { useCompetitionsNavVisible } from '@ping/api/campaigns';
import { GovernanceRequestNotice } from '@ping/components/GovernanceRequestNotice';
import { Page } from '@ping/components';
import { USER_COMPETITIONS_PATH } from '@ping/constants/campaigns.constant';
import { signoutReset, t } from '@ping/helpers';
import {
  hasBotGatewayAccessSelector,
  isBusinessLoginSelector,
  isUserActivityStatusActiveSelector,
  useUserInformationStore,
} from '@ping/stores/userInformation.store';
import { Sidebar } from '@ping/uikit';
import { clsx } from 'clsx';

import style from './style.module.scss';
import { UserLayoutContent } from './UserLayoutContent';
import { UserLayoutDescription } from './UserLayoutDescription';
import { UserLayoutSubtitle } from './UserLayoutSubtitle';
import { UserLayoutTitle } from './UserLayoutTitle';
import { PORTFOLIO_PATH } from '@ping/constants';
import { useBusinessKybLock } from '@ping/pages/business/verification/use-business-kyb-lock';

interface IUserLayoutProps extends ICustomizable {
  children: React.ReactNode;
}

export const UserLayout = (props: IUserLayoutProps) => {
  const isUserActivityStatusActive = useUserInformationStore(isUserActivityStatusActiveSelector);
  const isBusinessLogin = useUserInformationStore(isBusinessLoginSelector);
  const hasApiKeyPermission = useUserInformationStore(hasBotGatewayAccessSelector);
  const { showCompetitionsNav } = useCompetitionsNavVisible();
  const lockedToKyb = useBusinessKybLock();
  const productNavDisabled = lockedToKyb || !isUserActivityStatusActive;

  return (
    <Page className={style['user-layout']} private>
      <Sidebar className={style['sidebar']}>
        <Sidebar.Section>
          <Sidebar.Item path={PORTFOLIO_PATH} isDisabled={lockedToKyb}>
            <PortfolioIcon />
            <span>{t('Portfolio')}</span>
          </Sidebar.Item>
          {showCompetitionsNav && (
            <Sidebar.Item path={USER_COMPETITIONS_PATH} isDisabled={productNavDisabled}>
              <Trophy size={20} />
              <span>{t('Competitions')}</span>
            </Sidebar.Item>
          )}
          <Sidebar.Item path='/user/history' isDisabled={productNavDisabled}>
            <HistoryIcon />
            <span>{t('History')}</span>
          </Sidebar.Item>
          <Sidebar.Item path='/user/payments' isDisabled={productNavDisabled}>
            <CardIcon />
            <span>{t('Payments')}</span>
          </Sidebar.Item>
          <Sidebar.Item path='/user/rewards' isDisabled={productNavDisabled}>
            <Gift size={20} />
            <span>{t('Rewards')}</span>
          </Sidebar.Item>
          <Sidebar.Item path='/user/referrals' isDisabled={productNavDisabled}>
            <ReferralsIcon />
            <span>{t('Referrals')}</span>
          </Sidebar.Item>
          {hasApiKeyPermission && (
            <Sidebar.Item path='/user/api-keys' isDisabled={productNavDisabled}>
              <ApiKeysIcon />
              <span>{t('API Keys')}</span>
            </Sidebar.Item>
          )}
          <Sidebar.Item path='/user/addressbook' isDisabled={productNavDisabled}>
            <AddressBookIcon />
            <span>{t('Address Book')}</span>
          </Sidebar.Item>
          {isBusinessLogin && (
            <Sidebar.Item path={'/user/business' as LinkProps['href']} isDisabled={productNavDisabled}>
              <Building2 size={20} />
              <span>{t('Business')}</span>
            </Sidebar.Item>
          )}
          <Sidebar.Item path='/user/settings' isDisabled={productNavDisabled}>
            <SettingsIcon />
            <span>{t('Settings')}</span>
          </Sidebar.Item>
        </Sidebar.Section>

        <Sidebar.Section className={style['second-sidebar']}>
          <Sidebar.Item path='/user/support' isDisabled={productNavDisabled}>
            <SupportIcon />
            <span>{t('Support')}</span>
          </Sidebar.Item>
          <Sidebar.Item className={style['logout']} path='/' onPress={() => signoutReset()}>
            <LogoutIcon />
            <span>{t('Logout')}</span>
          </Sidebar.Item>
        </Sidebar.Section>
      </Sidebar>

      <section className={clsx(style['user-layout__section'], props.className)}>
        <GovernanceRequestNotice />
        {props.children}
      </section>
    </Page>
  );
};

// TODO: 1. make it a general component to use them in admin section as well
// TODO: 2. come up with a general name maybe: "dashboard", "panel" or "layout" itself
UserLayout.Title = UserLayoutTitle;
UserLayout.Subtitle = UserLayoutSubtitle;
UserLayout.Description = UserLayoutDescription;
UserLayout.Content = UserLayoutContent;
